import * as React from 'react'

import { Popover as PopoverPrimitive } from '@base-ui/react/popover'
import { Info } from 'lucide-react'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import { Tag } from '~/client/src/shared/components/Tag/Tag'
import Localization from '~/client/src/shared/localization/LocalizationManager'

interface IChipProps {
  /** What the resource inherits, rendered inside the chip. */
  label: React.ReactNode
  /** The sentence before the Project Settings link, e.g. "Inherited from ... Manage it in". */
  description: React.ReactNode
  /**
   * When provided, the popover's "Project Settings" is a link that navigates to
   * the project settings where the working hours are edited. Omitted on surfaces
   * that can't navigate there (e.g. mobile), leaving it as plain text.
   */
  onEditProjectHours?: () => void
  /**
   * On mobile the working hours can't be managed in-app, so the popover points
   * the user to the desktop version instead of offering a link.
   */
  isMobile?: boolean
}

/**
 * A non-removable, non-editable chip showing a value a resource inherits from the
 * project rather than storing of its own. A trailing info icon sits where a removable "x" would; clicking the
 * chip opens a popover (styled like a dark tooltip) explaining the inheritance.
 * On desktop it offers a "Project Settings" link where navigation is available;
 * on mobile it points to the desktop version instead. The trigger stops click/key
 * propagation so opening the popover doesn't also open the cell's add-interval
 * editor (the cell's "+" affordance still adds a per-resource override).
 */
export function InheritedProjectChip({
  label,
  description,
  onEditProjectHours,
  isMobile,
}: IChipProps) {
  const { translator } = Localization

  const stopPropagation = (event: React.SyntheticEvent) =>
    event.stopPropagation()

  const stopKeyboardActivation = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.stopPropagation()
    }
  }

  const manageTarget =
    !isMobile && onEditProjectHours ? (
      <button
        type="button"
        className="tw:inline tw:cursor-pointer tw:whitespace-nowrap tw:border-0 tw:bg-transparent tw:p-0 tw:text-inherit tw:underline tw:underline-offset-2"
        onClick={onEditProjectHours}
      >
        {translator.projectSettings}
      </button>
    ) : (
      <span className="tw:whitespace-nowrap">
        {isMobile
          ? translator.projectSettingsOnDesktop
          : translator.projectSettings}
      </span>
    )

  return (
    <Popover>
      <PopoverTrigger
        render={<button type="button" />}
        className="tw:shrink-0 tw:cursor-pointer tw:border-0 tw:bg-transparent tw:p-0"
        onClick={stopPropagation}
        onPointerDown={stopPropagation}
        onKeyDown={stopKeyboardActivation}
      >
        <Tag
          // No fill: the inherited default reads as a hint rather than a set value,
          // so it doesn't compete with the real chips a resource's own hours produce.
          className="tw:bg-transparent"
          trailing={<Info className="tw:size-3.5 tw:text-muted-foreground" />}
        >
          {label}
        </Tag>
      </PopoverTrigger>
      {/* Dark bubble matching the tooltip look (bg-foreground/text-background,
          no ring/shadow) rather than the default light popover surface. */}
      <PopoverContent
        side="top"
        className="tw:w-fit tw:max-w-[260px] tw:rounded-md tw:bg-foreground tw:px-3 tw:py-1.5 tw:text-xs tw:text-background tw:shadow-none tw:ring-0"
      >
        {/* One flowing text node so the sentence stays on a single line. */}
        <span className="tw:text-left">
          {description} {manageTarget}
          {'.'}
        </span>
        <PopoverPrimitive.Arrow className="tw:z-50 tw:size-2.5 tw:translate-y-[calc(-50%-2px)] tw:rotate-45 tw:rounded-[2px] tw:bg-foreground tw:data-[side=bottom]:top-1 tw:data-[side=top]:-bottom-2.5" />
      </PopoverContent>
    </Popover>
  )
}

/**
 * The Hours-of-operation default: the project working hours a resource inherits while it
 * has no operating intervals of its own.
 */
export function InheritedHoursChip(props: Omit<IChipProps, 'description'>) {
  return (
    <InheritedProjectChip
      {...props}
      description={Localization.translator.inheritedHoursDescription}
    />
  )
}
